import React from 'react';
import { FormControl, Select, MenuItem, Typography, Box } from '@mui/material';
import { ChevronDown } from 'lucide-react';

/**
 * Single custom dropdown arrow using Lucide ChevronDown (13px).
 * Replaces the default MUI arrow so only ONE arrow exists.
 * Positioned at absolute right: 12px, top: 50%, transform: translateY(-50%)
 * to guarantee that the arrow is mathematically centered on the exact horizontal axis.
 */
const DropdownArrowIcon = (props) => (
    <ChevronDown
        size={13}
        color="#64748B"
        {...props}
        style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            transition: 'none',
        }}
    />
);

/**
 * FilterSelect
 * 
 * Reusable dropdown component ensuring:
 * 1. Text and arrow are aligned on the same horizontal axis and vertically centered.
 * 2. 8px minimum gap between text and arrow via pr: 32px.
 * 3. Ellipsis overflow prevention for long labels.
 * 4. 40px height, 8px border radius, flat styling with #E2E8F0 border, no box shadows.
 */
const FilterSelect = ({
    label = '',
    placeholder = '',
    value = '',
    onChange,
    options = [],
    minWidth = 130,
    sx = {},
    children
}) => {
    const emptyText = placeholder || label;

    // Helper to find display label for the currently selected value
    const getDisplayLabel = (val) => {
        if (!val && val !== 0) return emptyText;
        if (options && options.length > 0) {
            const found = options.find((opt) => String(opt.value) === String(val));
            if (found) return found.label;
        }
        return val;
    };

    const hasSelection = Boolean(value);

    return (
        <FormControl
            size="small"
            sx={{
                width: '100%',
                minWidth,
                ...sx
            }}
        >
            <Select
                displayEmpty
                value={value}
                onChange={onChange}
                IconComponent={DropdownArrowIcon}
                renderValue={(selected) => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            height: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <Typography
                            noWrap
                            sx={{
                                fontSize: '0.85rem',
                                fontWeight: hasSelection ? 500 : 400,
                                color: hasSelection ? '#1E293B' : '#64748B',
                                lineHeight: '40px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {getDisplayLabel(selected)}
                        </Typography>
                    </Box>
                )}
                sx={{
                    borderRadius: '8px',
                    height: '40px',
                    fontSize: '0.85rem',
                    backgroundColor: '#FFFFFF',
                    boxShadow: 'none',
                    '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        height: '40px !important',
                        py: '0 !important',
                        pl: '14px !important',
                        pr: '32px !important', // Guarantees 8px space before 13px arrow at right: 12px
                        boxSizing: 'border-box',
                        lineHeight: '40px',
                    },
                    '& .MuiSelect-icon': {
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        right: '12px',
                        width: '13px',
                        height: '13px',
                        color: '#64748B',
                        pointerEvents: 'none',
                    },
                    '& fieldset': {
                        borderColor: '#E2E8F0',
                        borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                        borderColor: '#CBD5E1 !important',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: '#FFBD65 !important',
                        borderWidth: '1px !important',
                    },
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            maxHeight: 320,
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'none',
                            mt: 0.5,
                            '& .MuiMenuItem-root': {
                                fontSize: '0.85rem',
                                py: 1,
                                px: 2,
                                '&.Mui-selected': {
                                    backgroundColor: '#FFF7ED',
                                    fontWeight: 500,
                                    color: '#803927',
                                    '&:hover': {
                                        backgroundColor: '#FFEDD5',
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: '#F8FAFC',
                                }
                            }
                        }
                    }
                }}
            >
                {options && options.length > 0
                    ? options.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))
                    : children}
            </Select>
        </FormControl>
    );
};

export default FilterSelect;
